import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { useId } from "react";

/** Label + control + helper/error. Wire every form field through this. */
export function FormRow({ label, help, error, required, children, htmlFor }: {
  label: ReactNode; help?: ReactNode; error?: ReactNode;
  required?: boolean; children: ReactNode; htmlFor?: string;
}) {
  return (
    <div className="form-row">
      <label className="field-label" htmlFor={htmlFor}>
        {label}{required && <span aria-hidden="true"> *</span>}
      </label>
      {children}
      {error ? (
        <span className="form-error" role="alert">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/></svg>
          {error}
        </span>
      ) : help ? (
        <span className="form-help">{help}</span>
      ) : null}
    </div>
  );
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={["field", "textarea", className].filter(Boolean).join(" ")} {...props} />;
}

type ToggleProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & { label: ReactNode };

export function Checkbox({ label, className, ...props }: ToggleProps) {
  return (
    <label className={["check", className].filter(Boolean).join(" ")}>
      <input type="checkbox" {...props} />
      <span>{label}</span>
    </label>
  );
}

export function Radio({ label, className, ...props }: ToggleProps) {
  return (
    <label className={["check", className].filter(Boolean).join(" ")}>
      <input type="radio" {...props} />
      <span>{label}</span>
    </label>
  );
}

/** Use for settings that apply immediately. For form fields, use Checkbox. */
export function Switch({ label, className, ...props }: ToggleProps) {
  return (
    <label className={["switch", className].filter(Boolean).join(" ")}>
      <input type="checkbox" role="switch" {...props} />
      <span>{label}</span>
    </label>
  );
}

/** Radio group with a shared name and an accessible group label. */
export function RadioGroup({ legend, name, options, value, onChange }: {
  legend: ReactNode; name: string;
  options: { value: string; label: ReactNode }[];
  value?: string; onChange?: (v: string) => void;
}) {
  const id = useId();
  return (
    <fieldset style={{ border: "none", padding: 0, margin: 0 }} aria-labelledby={id}>
      <span className="field-label" id={id}>{legend}</span>
      <div className="stack-tight">
        {options.map((o) => (
          <Radio key={o.value} name={name} label={o.label} value={o.value}
                 checked={value === o.value}
                 onChange={(e) => onChange?.(e.target.value)} />
        ))}
      </div>
    </fieldset>
  );
}
